import { scopedChannelApi } from '@/session/scopedClients';
import type { RequestScope } from '@/session/scopedTransport';
import type { ChannelApiResponse } from '@/types/endpoints';
import { utils } from '@/utils';
import type { ReadRequestOptions } from '../../requestOptions';
import type { ExpansionGridResponse, ExpansionRung } from './types';

/** The grid pages by FAMILY, never splitting one, so a page is at most this many families. */
export const RUN_GRID_MAX_PER_PAGE = 100;

/** The two statuses the grid filters by; a grouping or unplaced product has no category row to filter. */
export type ExpansionGridStatus = 'ready' | 'needs_input';

const validate = (runId: number, subscriptionId: number, groupingId: number, page?: number, perPage?: number) => {
  if (!runId || runId < 1) {
    throw new Error('Valid run ID is required');
  }
  if (!subscriptionId || subscriptionId < 1) {
    throw new Error('Valid subscription ID is required');
  }
  if (!groupingId || groupingId < 1) {
    throw new Error('Valid grouping ID is required');
  }
  if (page !== undefined && (!page || page < 1)) {
    throw new Error('page must be a positive number');
  }
  if (perPage !== undefined && (!perPage || perPage < 1 || perPage > RUN_GRID_MAX_PER_PAGE)) {
    throw new Error(`perPage must be between 1 and ${RUN_GRID_MAX_PER_PAGE}`);
  }
};

type RequestParams = {
  runId: number;
  /** Any subscription of the organization — see `getRunFamilies`. */
  subscriptionId: number;
  /** The destination category — one at a time. */
  groupingId: number;
  page?: number;
  /** Families per page. */
  perPage?: number;
  status?: ExpansionGridStatus;
  /** Only products with at least one gap (or, with `key`, a gap on that key). */
  gapsOnly?: boolean;
  rung?: ExpansionRung;
  /** A commonality key. */
  key?: string;
  search?: string;
};

/** One destination category's products as a grid: a column per attribute, a cell per value and where it came from. */
export const getRunGrid = async (
  scope: RequestScope,
  { runId, subscriptionId, groupingId, page, perPage, status, gapsOnly, rung, key, search }: RequestParams,
  options: ReadRequestOptions
): Promise<ChannelApiResponse<ExpansionGridResponse>> => {
  try {
    validate(runId, subscriptionId, groupingId, page, perPage);

    const result = await scopedChannelApi(scope).get<ExpansionGridResponse>(`atts/expansion/runs/${runId}/grid`, {
      ...options,
      headers: { 'x-astur-subscription-id': subscriptionId.toString() },
      params: {
        grouping_id: groupingId,
        page,
        per_page: perPage,
        status,
        // Sent only when on: the API reads presence, and `gaps_only=0` is not a value it promises to parse.
        gaps_only: gapsOnly ? 1 : undefined,
        rung,
        key: key || undefined,
        search: search || undefined,
      },
    });

    return { data: result.data, status: result.status };
  } catch (e) {
    return utils.error.handleAxiosError(e);
  }
};
